import type { ReactNode } from 'react';
import { Link, type To } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Badge, cn, formatInstantDate } from '@hrms/ui';
import {
  formatTrainingDuration,
  type TrainingDueOverlay,
  type TrainingLearnerStatus,
} from '@hrms/shared';
import { CourseCover } from './CourseCover.js';
import { LearnerStatusBadge } from './LearnerStatusBadge.js';

export interface CourseCardProps {
  title: string;
  category: string;
  coverImageUrl?: string | null;
  lessonCount: number;
  totalDurationSeconds: number;
  /** 0–100. The contract sends it as a decimal string; either form is accepted. Absent → no bar. */
  progressPercent?: number | string | null;
  /** With it, the meta line reads "3 of 8 lessons" instead of "8 lessons". */
  lessonsCompleted?: number | null;
  /** ISO instant; shown as "Due 20 Oct 2026" while the course is still open. */
  dueAt?: string | null;
  overlay?: TrainingDueOverlay;
  /** Absent on HR's library cards. `null` = a cancelled assignment ("Removed"). */
  learnerStatus?: TrainingLearnerStatus | null;
  late?: boolean;
  required?: boolean;
  /** The card's one action, e.g. "Continue", "Start", "Review", "Edit". */
  actionLabel: string;
  /** Makes the whole card a router link. Wins over `onClick`. */
  to?: To;
  onClick?: () => void;
  /** Rendered under the card, OUTSIDE the link — the place for a second control (a menu, stats). */
  footer?: ReactNode;
  className?: string;
}

function parsePercent(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : null;
}

/** "3 of 8 lessons · 1h 20m" / "8 lessons" / "1 lesson · 45m". */
export function courseMetaLine(
  lessonCount: number,
  totalDurationSeconds: number,
  lessonsCompleted?: number | null,
): string {
  const noun = lessonCount === 1 ? 'lesson' : 'lessons';
  const lessons =
    lessonsCompleted !== null && lessonsCompleted !== undefined && lessonCount > 0
      ? `${Math.min(lessonsCompleted, lessonCount)} of ${lessonCount} ${noun}`
      : `${lessonCount} ${noun}`;
  return totalDurationSeconds > 0
    ? `${lessons} · ${formatTrainingDuration(totalDurationSeconds)}`
    : lessons;
}

/**
 * A course as a card (PLAN §10 "Course cards"): 16:9 cover with a thin YouTube-style progress bar
 * on its bottom edge and a duration badge, a two-line title, "N lessons · 1h 20m", the due / status
 * chip and ONE action. The whole card is the link (or button), so the target is the full tile, not
 * a small word in its corner; anything else interactive goes in `footer`, which sits outside it so
 * controls are never nested inside a link.
 *
 * Status and due chips: an open course shows its due date (red "Overdue" / amber "Due soon" when the
 * overlay says so); a finished one shows its outcome instead, since a deadline no longer matters.
 */
export function CourseCard({
  title,
  category,
  coverImageUrl,
  lessonCount,
  totalDurationSeconds,
  progressPercent,
  lessonsCompleted,
  dueAt,
  overlay = null,
  learnerStatus,
  late = false,
  required = false,
  actionLabel,
  to,
  onClick,
  footer,
  className,
}: CourseCardProps) {
  const percent = learnerStatus === 'completed' ? 100 : parsePercent(progressPercent);
  const isOpen = learnerStatus === undefined || learnerStatus === 'not_started' || learnerStatus === 'in_progress';
  const showOutcome = learnerStatus !== undefined && !isOpen;
  const interactive = to !== undefined || onClick !== undefined;

  const body = (
    <>
      <span className="relative block">
        <CourseCover
          title={title}
          category={category}
          coverImageUrl={coverImageUrl}
          size="md"
          className="rounded-none"
        />
        {totalDurationSeconds > 0 && (
          <span
            className={cn(
              'absolute end-2 rounded-sm bg-player-surface/85 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-player-fg',
              percent !== null ? 'bottom-2.5' : 'bottom-2',
            )}
          >
            {formatTrainingDuration(totalDurationSeconds)}
          </span>
        )}
        {percent !== null && (
          <span
            role="progressbar"
            aria-label="Course progress"
            aria-valuenow={Math.round(percent)}
            aria-valuemin={0}
            aria-valuemax={100}
            className="absolute inset-x-0 bottom-0 block h-1 bg-progress-track"
          >
            <span
              className="block h-full bg-progress-fill transition-[width] duration-500 ease-out"
              style={{ width: `${percent}%` }}
            />
          </span>
        )}
      </span>

      <span className="flex flex-1 flex-col gap-1.5 p-4">
        {required && (
          <span className="block">
            <Badge tone="info">Required</Badge>
          </span>
        )}
        <span className="line-clamp-2 font-display text-base font-semibold leading-snug text-fg">
          {title}
        </span>
        <span className="block text-xs tabular-nums text-fg-muted">
          {courseMetaLine(lessonCount, totalDurationSeconds, lessonsCompleted)}
        </span>
        <span className="mt-auto flex flex-wrap items-center justify-between gap-2 pt-2">
          <span className="flex min-w-0 flex-wrap items-center gap-1.5">
            {showOutcome ? (
              <LearnerStatusBadge status={learnerStatus ?? null} late={late} />
            ) : (
              <DueChip dueAt={dueAt ?? null} overlay={overlay} />
            )}
          </span>
          <span
            className={cn(
              'ms-auto inline-flex items-center gap-0.5 text-sm font-semibold',
              interactive ? 'text-accent group-hover/card:text-accent-hover' : 'text-fg-muted',
            )}
          >
            {actionLabel}
            {interactive && <ChevronRight className="size-4 rtl:rotate-180" aria-hidden="true" />}
          </span>
        </span>
      </span>
    </>
  );

  // The focus ring is drawn by the card (`has-[:focus-visible]`), not the link: the card clips its
  // cover to its rounded corners, which would also clip a ring drawn on the link inside it.
  const targetClass = 'flex flex-1 flex-col text-start focus-visible:outline-none';

  return (
    <div
      className={cn(
        'group/card relative flex h-full flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-sm',
        'has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-accent',
        interactive &&
          'transition-[transform,box-shadow] duration-150 hover:-translate-y-0.5 hover:shadow-card motion-reduce:transition-none motion-reduce:hover:translate-y-0',
        className,
      )}
    >
      {to !== undefined ? (
        <Link to={to} className={targetClass}>
          {body}
        </Link>
      ) : onClick ? (
        <button type="button" onClick={onClick} className={cn(targetClass, 'cursor-pointer')}>
          {body}
        </button>
      ) : (
        <div className="flex flex-1 flex-col">{body}</div>
      )}
      {footer && <div className="border-t border-border px-4 py-3">{footer}</div>}
    </div>
  );
}

function DueChip({ dueAt, overlay }: { dueAt: string | null; overlay: TrainingDueOverlay }) {
  if (overlay === 'overdue') {
    return (
      <Badge tone="danger" className="font-semibold">
        Overdue{dueAt ? ` · ${formatInstantDate(dueAt)}` : ''}
      </Badge>
    );
  }
  if (overlay === 'due_soon') {
    return (
      <Badge tone="warning" className="font-semibold">
        Due soon{dueAt ? ` · ${formatInstantDate(dueAt)}` : ''}
      </Badge>
    );
  }
  if (dueAt) return <Badge tone="neutral">Due {formatInstantDate(dueAt)}</Badge>;
  return null;
}
